/*
 * @providesModule AeropayController
 *
 */

import React from 'react';
import SMSVerification from 'SMSVerification';
import ClientPropertyConstants from 'ClientPropertyConstants';
import NavigationManager from 'NavigationManager';
import {StoreInfoObject} from 'StoreInfo';
import CurrentUserStore from 'CurrentUserStore';
import SKZPersonalInfo from 'SKZPersonalInfo';
import LoadingIndicator from 'LoadingIndicator';
import AeropayAPIController from './AeropayAPIController';
import AuthenticationController from 'AuthenticationController';
import UserInformationScreen from '../CheckoutDepositScreen/AchDepositFlow/UserInformationScreen';
import {
  AEROPAY_DEFAULT_FIRST_DEPOSIT_BONUS_PERCENTAGE,
  AeropayACHDepositFlowResult,
  EBankLinkDepositScreen,
  GetAerosyncURLResponse,
  AEROPAY_BANK_LINKING_ERROR_MESSAGE,
  getConfigurationId,
  getAerosyncEnv,
  AeropayError,
} from './AeropayController.types';
import AeropayMFAScreen from '../CheckoutDepositScreen/AchDepositFlow/AeropayMFAScreen';
import {_m} from 'INTL';
import {ErrorResponse} from 'NetClientTypes';
import {NativeModules, TouchableOpacity} from 'react-native';
import DeepLinkUtils from 'DeepLinkUtils';
import Preferences from 'Preferences';
import SKZConstants from 'SKZConstants';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import SKZToaster from 'SKZToaster';
import {SuccessEventType, WidgetEventType, AeroSyncWidget} from 'aerosync-react-native-sdk';
import DataDogRUM from 'DataDogRUM';
import AWSManager from 'AWSManager';
import {PaymentErrorModal} from 'PaymentErrorModal';
import ModalNavigationController, {ModalProps} from 'ModalNavigationController';
import {updateUserPersonalInfoMutation} from '@api/User';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';
const {textColorPrimary} = SkillzStyle.fullStyles;

const SkillzModule = NativeModules.SkillzModule;
const bundleId = SkillzModule.App.PackageId;

const AEROPAY = 'AEROPAY';
export const MAX_ALLOWED_BANK_ACCOUNTS = 5;
const BANK_PROMO_HIGHLIGHT_LIMIT = 3;
class AeropayController {
  isAeropayAvailable(paymentTypes: string[]) {
    return paymentTypes.includes(AEROPAY) && ClientPropertyConstants.getIsACHDepositEnabled();
  }

  isUserEligibleForAchExtraBonusCash(storeInfo?: StoreInfoObject): boolean {
    if (!storeInfo?.data?.aeropay) {
      console.debug(
        'AeropayController: User not eligible to extra ACH bonus cash - No store info available.'
      );
      return false;
    }

    if (!this.isAeropayAvailable(storeInfo.data.payment_types)) {
      console.debug(
        'AeropayController: User not eligible to extra ACH bonus cash - Aeropay not available.'
      );
      return false;
    }

    if (storeInfo.data.aeropay.user_has_deposited) {
      console.debug(
        'AeropayController: User not eligible to extra ACH bonus cash - Not first-time ACH depositor.'
      );
      return false;
    }

    console.debug(
      'AeropayController: User is eligible to extra ACH bonus cash. ',
      storeInfo?.data?.aeropay?.first_deposit_bonus_percentage
    );
    return true;
  }

  getAchExtraBonus(
    storeInfo?: StoreInfoObject,
    amount?: number
  ): {amount: number; percentageLabel: number} {
    const isUserEligible = this.isUserEligibleForAchExtraBonusCash(storeInfo);
    if (!isUserEligible) {
      console.debug(
        'AeropayController: User not eligible to extra ACH bonus cash - returning default bonus.'
      );
      return {
        amount: 0,
        percentageLabel: 0,
      };
    }

    if (!storeInfo || !amount) {
      console.error(
        'AeropayController: User eligible to extra ACH bonus cash - No store info or amount available.'
      );
      return {
        amount: 0,
        percentageLabel: 0,
      };
    }

    const percentage = this.getAchExtraBonusCashPercentage(storeInfo);
    const percentageLabel = percentage * 100;
    const bonusAmount = amount * (percentage ?? 0);

    console.debug('AeropayController: Getting ACH extra bonus cash.', bonusAmount, percentage);
    return {
      amount: bonusAmount,
      percentageLabel: percentageLabel,
    };
  }

  getAchExtraBonusCashPercentage(storeInfo?: StoreInfoObject) {
    const isUserEligible = this.isUserEligibleForAchExtraBonusCash(storeInfo);
    if (!isUserEligible) {
      console.debug(
        'AeropayController: User not eligible to extra ACH bonus cash - returning default percentage.'
      );
      return AEROPAY_DEFAULT_FIRST_DEPOSIT_BONUS_PERCENTAGE;
    }

    if (!storeInfo) {
      console.error(
        'AeropayController: User eligible to extra ACH bonus cash - No store info available.'
      );
      return AEROPAY_DEFAULT_FIRST_DEPOSIT_BONUS_PERCENTAGE;
    }

    console.debug(
      'AeropayController: Getting ACH extra bonus cash percentage.',
      storeInfo?.data?.aeropay?.first_deposit_bonus_percentage ??
        AEROPAY_DEFAULT_FIRST_DEPOSIT_BONUS_PERCENTAGE,
      storeInfo?.data?.aeropay?.first_deposit_bonus_percentage,
      AEROPAY_DEFAULT_FIRST_DEPOSIT_BONUS_PERCENTAGE
    );
    return (
      storeInfo?.data?.aeropay?.first_deposit_bonus_percentage ??
      AEROPAY_DEFAULT_FIRST_DEPOSIT_BONUS_PERCENTAGE
    );
  }

  // Methods to handle the auto highlighting count of the ACH bank promo option on the saved payments screen
  iterateBankPromoHighlightCount() {
    const currentCount = this.getHighlightBankPromoCount();

    this.shouldHighlightBankPromo() &&
      Preferences.set({[SKZConstants.CHECKOUT_DEFAULT_BANK_PROMO_TIMES]: currentCount + 1});
  }

  shouldHighlightBankPromo(): boolean {
    const currentCount = this.getHighlightBankPromoCount();
    console.debug('SHOULD_HIGHLIGHT_BANK_PROMO: ', currentCount < BANK_PROMO_HIGHLIGHT_LIMIT);

    return currentCount < BANK_PROMO_HIGHLIGHT_LIMIT;
  }

  getHighlightBankPromoCount(): number {
    const currentCount = Preferences.get(SKZConstants.CHECKOUT_DEFAULT_BANK_PROMO_TIMES) ?? 0;
    console.debug('BANK_PROMO_AUTO_HIGHLIGHTED COUNT: ', currentCount);
    return currentCount;
  }

  /**
   * This method is the entry point for the Aeropay ACH deposit flow.
   * It will handle the entire flow from user information collection to bank linking and transaction processing.
   * If the user has already linked a bank account, it will skip the user information collection and return a special
   * response AeropayACHDepositFlowResult indicating for the caller to display the bank account selection on CheckoutDepositScreen.
   * If the Aeropay deposit is not available, it will throw an error, indicating that the caller should try another payment method.
   * @param storeInfo
   * @param fromWhere (Optional) Default 'DEPOSIT'. Determines from what flow is running the ACH bank link flow.
   * @returns AeropayACHDepositFlowResult
   */
  async launchACHBankLinkFlow(
    storeInfo?: StoreInfoObject,
    fromWhere: 'DEPOSIT' | 'WITHDRAWAL' = 'DEPOSIT'
  ): Promise<AeropayACHDepositFlowResult> {
    console.debug('AeropayController: Starting ACH Bank Link flow');

    // Extra bonus cash toast
    const eligibleForExtraBonus = this.isUserEligibleForAchExtraBonusCash(storeInfo);
    const bonusPercentage = this.getAchExtraBonusCashPercentage(storeInfo) * 100;

    // show extra bonus cash promotion applied toast from 'DEPOSIT' flow if eligible
    if (fromWhere === 'DEPOSIT' && eligibleForExtraBonus && bonusPercentage > 0) {
      setTimeout(() => {
        SKZToaster.show('success', _m('Promotion Applied! {0}% Bonus Cash!', bonusPercentage));
      }, 600);
    }

    try {
      if (!this.isAeropayAvailable(storeInfo?.data.payment_types || [])) {
        return this.showErrorMessage(_m('Please try another payment method.')).then(() => {
          // we need to throw an error to stop the flow and let the caller select another payment method
          throw new Error('Aeropay is not available');
        });
      }

      let userPersonalInfo = await this.fetchUpdatedPersonalInfo();
      const userId = await CurrentUserStore.getCurrentUserId();
      const existingAeropayUserId = storeInfo?.data?.aeropay?.user_id ?? '';
      const isNewAeropayUser = !existingAeropayUserId;

      /**
       * Phone Verification
       * If the user has not verified their phone number, we need to display the phone verification screen before proceeding
       * and update the user personal info with the verified phone number.
       * If the user dismisses the phone verification screen, we need to stop the flow and let the caller select another payment method.
       */
      if (!userPersonalInfo.phone_number || !userPersonalInfo.phone_verified) {
        const phoneVerified = await this.displayPhoneVerify(userPersonalInfo);

        // user has dismissed the phone verification screen
        if (!phoneVerified) {
          NavigationManager.pop();
          throw new Error('Phone verification dropped.');
        }

        // update the user personal info with the verified phone
        userPersonalInfo = await this.fetchUpdatedPersonalInfo();
      }

      if (isNewAeropayUser) {
        const aeropayUserOnboardingResult =
          await this.launchAeropayUserOnboarding(userPersonalInfo);

        if (aeropayUserOnboardingResult?.action) {
          return aeropayUserOnboardingResult;
        }
      }

      LoadingIndicator.show();
      const aerosyncCredentials = await AeropayAPIController.getAerosyncURL().catch(
        (error: AeropayError) => {
          LoadingIndicator.hide();
          this.showErrorMessage(AEROPAY_BANK_LINKING_ERROR_MESSAGE, error);
          console.error(`Error on getting Aerosync URL: ${error}`);
          return null;
        }
      );

      if (!aerosyncCredentials) {
        // dismiss user information screen if it's still presented
        NavigationManager.isSceneCurrentlyPresented(EBankLinkDepositScreen.UserInformation) &&
          NavigationManager.pop();

        // let the caller know if the flow droped after creating the Aeropay user
        return Promise.resolve({
          action: 'ACH_DEPOSIT_DISMISSED',
          isNewAeropayUser,
        });
      }
      LoadingIndicator.hide();

      const widgetResult = (await this.displayAeropayWidget(
        isNewAeropayUser,
        aerosyncCredentials,
        userId
      )
        .catch((error) => {
          throw new Error('Error on Aeropay widget: ' + error);
        })
        .finally(() =>
          DataDogRUM.stopView('AEROPAY_AEROSYNC_EVENT', {
            result: 'AEROPAY_ACH_DEPOSIT_DISMISSED',
            type: 'AEROPAY_ACH_DEPOSIT_DISMISSED',
            time: Date.now(),
          })
        )) as SuccessEventType & AeropayACHDepositFlowResult;

      if (widgetResult.action) {
        return Promise.resolve(widgetResult);
      }

      // at this point the user has successfully linked a bank account and tapped the 'Continue' button

      LoadingIndicator.show();

      const {bank_account_id} = await AeropayAPIController.createAeropayBankAccount(
        widgetResult
      ).catch((error) => {
        trackTelemetryEvent({
          event: TelemetryEventName.STORE_BANK_LINKING_FAIL,
          includeUserWalletDetails: true,
          payload: {
            error_message: error?.message ?? '',
            error_code: error?.code ?? '',
          },
        });
        this.showErrorMessage(AEROPAY_BANK_LINKING_ERROR_MESSAGE, error);
        throw new Error(`Error on linking bank account: ${error}`);
      });

      trackTelemetryEvent({
        event: TelemetryEventName.STORE_BANK_LINKING_COMPLETE,
        includeUserWalletDetails: true,
      });
      return Promise.resolve({
        action: 'CREATED_BANK_ACCOUNT',
        bankAccountId: bank_account_id,
      });
    } catch (error) {
      LoadingIndicator.hide();
      console.error('AeropayController: ACH flow stopped due to error:', JSON.stringify(error));
      throw error;
    }
  }

  private async launchAeropayUserOnboarding(
    userPersonalInfo: SKZPersonalInfo
  ): Promise<AeropayACHDepositFlowResult | void> {
    // show user information screen if there is any missing information, resolves with updated user info
    const updatedUserPersonalInfo =
      await this.displayUserInformationScreenIfNeeded(userPersonalInfo);

    if (!userPersonalInfo.email) {
      await updateUserPersonalInfoMutation.mutate({
        variables: {
          data: {
            email: updatedUserPersonalInfo.email,
          },
        },
        onError: (error) => {
          this.showErrorMessage(_m('Error updating your user info.\nPlease try again later.'));
        },
      });
    }

    LoadingIndicator.show();
    const aeropayNewUserResponse = await AeropayAPIController.createAeropayUser(
      updatedUserPersonalInfo
    )
      .catch((error) => {
        return this.showErrorMessage(AEROPAY_BANK_LINKING_ERROR_MESSAGE, error).then(() => {
          NavigationManager.pop();
          throw new Error(`Error on creating Aeropay user: ${error}`);
        });
      })
      .finally(() => {
        LoadingIndicator.hide();
      });

    if (aeropayNewUserResponse.shouldShowMFAFlow) {
      await this.displayMFAScreen(
        aeropayNewUserResponse.userPersonalInfo,
        aeropayNewUserResponse.MFASubtitleText
      );

      return Promise.resolve({
        action: 'MFA_COMPLETED',
      });
    }

    return Promise.resolve();
  }

  private async displayPhoneVerify(userPersonalInfo: SKZPersonalInfo): Promise<boolean> {
    /**
     * TODO: To be verified with Product: Should we display the whole save account flow or just the phone verification is fine?
     */

    const rightIconStyles = {
      height: 18,
      width: 18,
      marginRight: 16,
    };

    return new Promise((resolve) => {
      const component = (
        <SMSVerification
          showCloseButton={true}
          rightIconComponent={
            <TouchableOpacity style={rightIconStyles} onPress={() => resolve(false)}>
              <SkillzImage
                source={'close_button_icon.svg'}
                style={rightIconStyles}
                tintColor={textColorPrimary.color}
              />
            </TouchableOpacity>
          }
          onClose={() => resolve(false)}
          onDone={() => {
            resolve(true);
          }}
          onPhoneNumberInUse={(phone, country, countryValue) => {
            AuthenticationController.displaySignInScreen(
              false,
              {
                phoneNumber: phone,
                country: country,
                countryValue: countryValue,
              },
              undefined,
              undefined,
              undefined,
              undefined,
              true
            );
          }}
          clientProps={{
            header: ClientPropertyConstants.getSMSPromptDepositHeader(),
            body: ClientPropertyConstants.getSMSPromptDepositBody(),
          }}
        />
      );
      NavigationManager.pushComponentWithScene(component, true, EBankLinkDepositScreen.SMSVerify);
    });
  }

  private displayUserInformationScreenIfNeeded(
    userPersonalInfo: SKZPersonalInfo
  ): Promise<SKZPersonalInfo> {
    /**
     * If we already have all required user information, we can skip the user information screen
     */
    if (
      userPersonalInfo.first_name &&
      userPersonalInfo.last_name &&
      userPersonalInfo.phone_number &&
      userPersonalInfo.email
    ) {
      return Promise.resolve(userPersonalInfo);
    }

    return new Promise((resolve, reject) => {
      const component = (
        <UserInformationScreen
          currentUserInfo={userPersonalInfo}
          onBackNavigation={() => {
            NavigationManager.pop();
            reject();
          }}
          onUpdateUserInfoError={this.showErrorMessage}
          handleComplete={(formData) => {
            resolve({
              ...userPersonalInfo,
              ...formData,
            } as SKZPersonalInfo);
          }}
        />
      );

      if (NavigationManager.isSceneCurrentlyPresented(EBankLinkDepositScreen.SMSVerify)) {
        NavigationManager.replaceWithTransition(component, EBankLinkDepositScreen.UserInformation);
      } else {
        NavigationManager.pushComponentWithScene(
          component,
          true,
          EBankLinkDepositScreen.UserInformation
        );
      }
    });
  }

  private displayMFAScreen(userPersonalInfo: SKZPersonalInfo, subtitleText: string): Promise<void> {
    return new Promise((resolve) => {
      const component = (
        <AeropayMFAScreen
          currentUserInfo={userPersonalInfo}
          displayMessage={subtitleText}
          hasBackButton={false}
          onComplete={() => {
            resolve();
          }}
        />
      );
      if (
        NavigationManager.isOneOfScenesCurrentlyPresented([
          EBankLinkDepositScreen.UserInformation,
          EBankLinkDepositScreen.SMSVerify,
        ])
      ) {
        NavigationManager.replaceWithTransition(component, EBankLinkDepositScreen.MFA);
      } else {
        NavigationManager.pushComponentWithScene(component, true, EBankLinkDepositScreen.MFA);
      }
    });
  }

  private displayAeropayWidget(
    isNewAeropayUser: boolean,
    aerosyncCredentials: GetAerosyncURLResponse,
    currentUserId: string
  ): Promise<SuccessEventType | AeropayACHDepositFlowResult> {
    const aerosyncEnv = getAerosyncEnv();
    const configurationId = getConfigurationId();

    return new Promise(
      (resolve: (event: SuccessEventType | AeropayACHDepositFlowResult) => void, reject) => {
        const aerosyncComponent = (
          <AeroSyncWidget
            environment={aerosyncEnv}
            token={aerosyncCredentials.token}
            deeplink={DeepLinkUtils._getSkillzDeepLink('', bundleId)}
            onClose={() => {
              NavigationManager.pop();
              resolve({
                action: 'ACH_DEPOSIT_DISMISSED',
                isNewAeropayUser,
              });
            }}
            onError={reject}
            onEvent={(event: WidgetEventType) => {
              DataDogRUM.startView('AEROPAY_AEROSYNC_EVENT', {
                result: event.pageTitle,
                data: event.onLoadApi,
                time: Date.now(),
              });

              const awsObject = JSON.stringify({
                USER_ID: currentUserId,
                PAGE_TITLE: event.pageTitle,
              });
              AWSManager.trackEvent('AEROPAY_AEROSYNC_EVENT', {result: awsObject});

              trackTelemetryEvent({
                event: TelemetryEventName.AEROPAY_AEROSYNC_EVENT,
                includeUserWalletDetails: true,
                payload: {
                  metaData: awsObject,
                },
              });

              console.debug('Aerosync event:', event);
            }}
            onLoad={() => console.debug('Aerosync widget loaded')}
            onSuccess={(event: SuccessEventType) => {
              resolve(event);
            }}
            consumerId={configurationId}
          />
        );

        const previousScenes = [
          EBankLinkDepositScreen.SMSVerify,
          EBankLinkDepositScreen.UserInformation,
          EBankLinkDepositScreen.MFA,
        ];

        if (NavigationManager.isOneOfScenesCurrentlyPresented(previousScenes)) {
          NavigationManager.replaceWithTransition(
            aerosyncComponent,
            EBankLinkDepositScreen.BankLinkWidget
          );
        } else {
          NavigationManager.pushComponentWithScene(
            aerosyncComponent,
            true,
            EBankLinkDepositScreen.BankLinkWidget
          );
        }
      }
    );
  }

  private fetchUpdatedPersonalInfo(): Promise<SKZPersonalInfo> {
    LoadingIndicator.show();
    return CurrentUserStore.fetchUserPersonalInfo(false)
      .catch((error: ErrorResponse) => {
        return this.showErrorMessage(_m('Please try another payment method.')).then(() => {
          // we need to throw an error to stop the flow and let the caller select another payment method
          throw new Error('Error on fetching user personal info.');
        });
      })
      .finally(() => LoadingIndicator.hide());
  }

  /**
   * This method will display Aeropay error message modal with the provided message.
   * @param message
   * @param error (optional) AeropayError
   * @returns Promise<void> - resolves when the modal is dismissed
   */
  showErrorMessage(message: string, error?: AeropayError, titleOverride?: string): Promise<void> {
    const defaultTitle = _m('Account Link Failed');
    const finalMessage = error?.code === 'AEROPAY_ERROR' ? error.message : message;

    return new Promise((resolve) => {
      const modalProps: ModalProps = {
        animationType: 'fade',
        transparent: true,
      };
      const paymentErrorModal = (
        <PaymentErrorModal
          errorTitle={titleOverride ?? defaultTitle}
          errorMessage={finalMessage}
          onDismiss={() => ModalNavigationController.hideModal(resolve)}
        />
      );
      ModalNavigationController.showModal(paymentErrorModal, modalProps);
    });
  }
}

export default new AeropayController();
